import CheckIcon from '@mui/icons-material/Check';
import { Button, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BaseEmployeeDTO, EmployeeDTOLegalTermsActionEnum } from 'probonio-shared-ui/api';
import { LoadingButton } from 'probonio-shared-ui/component/button';
import { apis } from 'probonio-shared-ui/module/api';
import { catchNotFound } from 'probonio-shared-ui/module/error';
import { loadMe, useTenant } from 'probonio-shared-ui/module/me';
import React, { useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../redux/hooks';
import { sessionStorageSkippedKey } from './legalTerms.const';
interface Props {
  employee: BaseEmployeeDTO;
  tenantId: string;
  action: EmployeeDTOLegalTermsActionEnum;
  align?: 'center' | 'left';
}

export const LegalTermsInfo: React.FC<Props> = ({ tenantId, employee, action, align = 'left' }) => {
  const { tenant } = useTenant();
  const { t } = useTranslation();

  const [isChecked, setIsChecked] = useState(false);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const handleCheckboxChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setIsChecked(event.target.checked);
  }, []);

  const { data: latestTerms } = useQuery({
    queryKey: ['legalTerms', apis.legalTerms.getLatestLegalTerms.name],
    queryFn: () =>
      apis.legalTerms
        .getLatestLegalTerms({ legalType: 'USER_TERMS', countryCode: tenant?.countryCode })
        .then(res => res.data)
        .catch(catchNotFound),
  });

  const consent = useMutation({
    mutationFn: async () => {
      await apis.legalTerms.createLegalTermsConsentLog({
        tenantId,
        createLegalTermsConsentLogDTO: { legalTermsId: latestTerms!.id },
      });
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      await dispatch(loadMe());
    },
  });

  const handleConsent = useCallback(() => {
    consent.mutate();
  }, [consent]);

  const handleSkip = useCallback(() => {
    sessionStorage.setItem(sessionStorageSkippedKey, employee.id);
    void queryClient.invalidateQueries({ queryKey: ['me'] });
    navigate(0);
  }, [navigate, queryClient, employee.id]);

  return (
    <Stack textAlign={align}>
      <Typography textAlign={align} data-test-id="legal-terms-version">
        {latestTerms ? (
          <>
            <Trans i18nKey="legalTerms:version" values={{ version: latestTerms.version }}>
              <a href={latestTerms.fileUrl} target="_blank" rel="noreferrer" />
            </Trans>
          </>
        ) : (
          <Trans i18nKey="legalTerms:noTerms" />
        )}
      </Typography>
      <Stack justifyContent={align}>
        {action === EmployeeDTOLegalTermsActionEnum.None && employee.legalTermsLastConsentDate && (
          <Typography marginTop={1} component="span" textAlign={align} data-test-id="legal-terms-consent-done">
            <CheckIcon color="success" fontSize="small" style={{ verticalAlign: 'middle', marginBottom: '3px', marginRight: 5 }} />
            <Trans i18nKey="legalTerms:actions.consentDone" values={{ lastDate: new Date(employee.legalTermsLastConsentDate) }} />
          </Typography>
        )}
        {latestTerms && action !== EmployeeDTOLegalTermsActionEnum.None && (
          <Stack alignItems={align}>
            <FormControlLabel
              sx={{ paddingTop: 1 }}
              control={<Checkbox checked={isChecked} data-test-id="legal-terms-chbx" onChange={handleCheckboxChange} />}
              label={<Typography component="span">{t('legalTerms:actions.checkLabel')}</Typography>}
            />
            <Grid container justifyContent="center" display="flex" gap={1} marginTop={2}>
              {action === EmployeeDTOLegalTermsActionEnum.Skip && (
                <Button variant="outlined" data-test-id="legal-terms-skip" size="small" onClick={handleSkip}>
                  <Typography fontWeight="bold">
                    <Trans i18nKey="legalTerms:actions.skip" />
                  </Typography>
                </Button>
              )}
              <LoadingButton
                size="small"
                type="submit"
                variant="contained"
                dataTestId="legal-terms-accept"
                onClick={handleConsent}
                disabled={!isChecked}
              >
                <Typography fontWeight="bold">
                  <Trans i18nKey="legalTerms:actions.accept" />
                </Typography>
              </LoadingButton>
            </Grid>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
